import { formatMinor, INVOICE_STATUS_LABELS, type InvoiceRow } from '@hvac/contracts';
import { Button, Card, CardBody, CardHeader, PageHeader, Skeleton } from '@hvac/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { invoicesQuery } from '../../shared/api/invoicing-queries.js';
import { sessionQuery } from '../../shared/api/queries.js';

/**
 * Every invoice the business has written: drafts first, then newest first,
 * each with what has been paid against it from the bank and what is still
 * owed. Nothing here is typed twice: paid comes from matched deposits.
 */
export function InvoicesPage() {
  const { data: session } = useSuspenseQuery(sessionQuery());
  const organizationId = session.activeOrganizationId ?? '';
  const membership = session.organizations.find(
    (entry) => entry.organization.id === organizationId,
  );
  const canWrite = membership?.permissions.includes('transaction:update') ?? false;
  const navigate = useNavigate();
  const invoices = useQuery(invoicesQuery(organizationId));

  const rows = invoices.data ?? [];
  const owed = rows.reduce((sum, row) => sum + BigInt(row.owedMinor), 0n);
  const overdue = rows.filter((row) => (row.daysOverdue ?? 0) > 0);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Business"
        title="Invoices"
        description={
          invoices.data === undefined
            ? 'Reading…'
            : rows.length === 0
              ? 'Nothing billed yet. An invoice you issue here becomes a signed income record the moment you issue it, and shows as owed until the deposit that pays it is matched on the Bank page.'
              : owed === 0n
                ? `${String(rows.length)} invoice${rows.length === 1 ? '' : 's'}, nothing outstanding.`
                : `${formatMinor(owed.toString())} outstanding${overdue.length === 0 ? ', none of it past due.' : `, ${String(overdue.length)} invoice${overdue.length === 1 ? '' : 's'} past due.`}`
        }
        action={
          canWrite ? (
            <Button onClick={() => void navigate({ to: '/invoices/new' })}>New invoice</Button>
          ) : undefined
        }
      />

      <Card>
        <CardHeader title="All invoices" description="Drafts first, then newest first." />
        <CardBody>
          {invoices.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-content-muted">
              {canWrite
                ? 'Write the first one with "New invoice". Numbering and the details printed at the top are in Settings → Invoicing.'
                : 'No invoices yet.'}
            </p>
          ) : (
            <table className="w-full text-sm tabular">
              <thead>
                <tr className="text-left text-xs text-content-muted">
                  <th className="py-1 font-medium">Number</th>
                  <th className="py-1 font-medium">Customer</th>
                  <th className="py-1 font-medium">Dated</th>
                  <th className="py-1 font-medium">Due</th>
                  <th className="py-1 font-medium">Status</th>
                  <th className="py-1 text-right font-medium">Total</th>
                  <th className="py-1 text-right font-medium">Owed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <InvoiceLine key={row.id} row={row} />
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function InvoiceLine({ row }: { row: InvoiceRow }) {
  const late = (row.daysOverdue ?? 0) > 0;
  return (
    <tr className="border-t border-border">
      <td className="py-1.5">
        <Link
          to="/invoices/$invoiceId"
          params={{ invoiceId: row.id }}
          className="font-medium text-brand-700 hover:underline"
        >
          {row.invoiceNumber ?? 'Draft'}
        </Link>
      </td>
      <td className="py-1.5">{row.customerName}</td>
      <td className="py-1.5 text-content-muted">{row.issuedOn}</td>
      <td className={['py-1.5', late ? 'text-danger' : 'text-content-muted'].join(' ')}>
        {row.dueOn ?? '—'}
        {late ? ` (${String(row.daysOverdue)}d late)` : ''}
      </td>
      <td className="py-1.5">{INVOICE_STATUS_LABELS[row.status]}</td>
      <td className="py-1.5 text-right">{formatMinor(row.grossMinor)}</td>
      <td className={['py-1.5 text-right font-medium', late ? 'text-danger' : ''].join(' ')}>
        {row.owedMinor === '0' ? '' : formatMinor(row.owedMinor)}
      </td>
    </tr>
  );
}
